import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FilterSidebar from './components/FilterSidebar';
import SearchBar from './components/SearchBar';
import ViewControls from './components/ViewControls';
import ModelCard from './components/ModelCard';
import QuickPreviewModal from './components/QuickPreviewModal';
import ComparisonTool from './components/ComparisonTool';
import PromotedProductsBanner from './components/PromotedProductsBanner';
import ComponentCard from './components/ComponentCard';
import ComponentDetailModal from './components/ComponentDetailModal';
import SellerOfferingCard from './components/SellerOfferingCard';
import SellerOfferingDetailModal from './components/SellerOfferingDetailModal';
import { supabase } from '../../lib/supabase';
import { productService } from '../../services/productService';
import { componentService } from '../../services/componentService';
import { sellerProductService } from '../../services/sellerProductService';
import { advertisementService } from '../../services/advertisementService';
import { userGarageService } from '../../services/userGarageService';
import { cartService } from '../../services/cartService';
import { searchService } from '../../services/searchService';
import { categoryService } from '../../services/categoryService';
import { userService } from '../../services/userService';
import { catalogCardService } from '../../services/catalogCardService';
import { useAuth } from '../../contexts/AuthContext';
import LoginSignupModal from '../../components/ui/LoginSignupModal';
import { imageUploadService } from '../../utils/imageUploadService';
import InlineBanner from './components/InlineBanner';
import ProductRequestModal from './components/ProductRequestModal';

const ModelCatalog = () => {
  const { user, userProfile, loading: authLoading, initialCheckComplete, isAuthenticated } = useAuth();
  const isAppendModeRef = useRef(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeMobileCategory, setActiveMobileCategory] = useState(null);
  const [mobileCategoryLimit, setMobileCategoryLimit] = useState(30);
  const [lastUpdatedTime, setLastUpdatedTime] = useState(new Date());

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const { comparison } = useParams();
  const navigate = useNavigate();

  const initialSearchTerm = searchParams.get('search') || '';
  const holidayParam = searchParams.get('holiday') || searchParams.get('holidy') || '';
  const seasonParam = searchParams.get('season') || '';

  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [searchHistory, setSearchHistory] = useState(() => {
    try {
      const saved = localStorage.getItem('modelCatalogSearchHistory');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [filters, setFilters] = useState({ categories: [], manufacturers: [], priceRange: null });
  
  // Initialize viewMode from sessionStorage, default to 'grid'
  const [viewMode, setViewMode] = useState(() => {
    return sessionStorage.getItem('modelCatalogViewMode') || 'grid';
  });

  // Save viewMode to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('modelCatalogViewMode', viewMode);
  }, [viewMode]);

  const [sortBy, setSortBy] = useState('relevance');
  const [itemsPerPage, setItemsPerPage] = useState(24);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedModels, setSelectedModels] = useState([]);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [previewModel, setPreviewModel] = useState(null);
  const [sellerModalItem, setSellerModalItem] = useState(null);
  const [selectedDetailComponent, setSelectedDetailComponent] = useState(null);
  const [comparisonModels, setComparisonModels] = useState([]);
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [componentData, setComponentData] = useState([]);
  const [totalComponents, setTotalComponents] = useState(0);
  const [showResultLimitWarning, setShowResultLimitWarning] = useState(false);
  const [collectionItems, setCollectionItems] = useState([]);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProductRequestModal, setShowProductRequestModal] = useState(false);
  const [draftProductRequest, setDraftProductRequest] = useState(null);
  const [authMode, setAuthMode] = useState('login');
  const [inlineBanner, setInlineBanner] = useState(null);
  const gridRef = useRef(null);
  const [columns, setColumns] = useState(4);
  const [categoryPreferences, setCategoryPreferences] = useState([]);
  const [brandPreferences, setBrandPreferences] = useState([]);
  const [popularSearches, setPopularSearches] = useState([]);
  const [quickFilters, setQuickFilters] = useState([]);
  const [publishedLayout, setPublishedLayout] = useState(null);

  const layoutKey = viewMode === 'component-list' ? 'componentCatalog' : 'modelCatalog';
  const layoutConfig = publishedLayout?.layouts?.[layoutKey];
  const gridStyle = (layoutConfig && (viewMode === 'grid' || viewMode === 'component-list')) ? {
    columnGap: layoutConfig.gridGapX || '24px',
    rowGap: layoutConfig.gridGapY || '24px',
    gridTemplateColumns: `repeat(auto-fill, minmax(${layoutConfig.cardWidth || '280px'}, 1fr))`
  } : undefined;

  // Dynamically compute exact resolved columns in the grid so inline banner aligns to full rows without gaps
  useEffect(() => {
    const calculateColumns = () => {
      if (!gridRef.current) return;
      const comp = window.getComputedStyle(gridRef.current);
      if (comp.gridTemplateColumns) {
        const count = comp.gridTemplateColumns.split(' ').filter(Boolean).length;
        if (count > 0) {
          setColumns(count);
        }
      }
    };

    calculateColumns();

    let ro = null;
    if (gridRef.current && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(calculateColumns);
      ro.observe(gridRef.current);
    }
    window.addEventListener('resize', calculateColumns);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', calculateColumns);
    };
  }, [gridStyle, viewMode, publishedLayout, products.length, componentData.length]);

  useEffect(() => {
    const fetchPublishedLayout = async () => {
      try {
        const layout = await catalogCardService.getPublished();
        if (layout && layout.config) {
          setPublishedLayout(layout.config);
        }
      } catch (err) {
        console.error('Failed to load published catalog layout:', err);
      }
    };

    const fetchBanner = async () => {
      try {
        const banners = await advertisementService.getActiveBanners();
        const activeInline = banners.filter(b => b.location === 'catalog_inline');
        
        // Sort newest banner to first left-side position (newest created_at first)
        activeInline.sort((a, b) => new Date(b.createdAt || b.created_at || 0) - new Date(a.createdAt || a.created_at || 0));

        if (activeInline.length > 0) {
          setInlineBanner({ isMultiBanner: true, banners: activeInline });
        } else {
          setInlineBanner(null);
        }
      } catch (err) {
        console.error('Failed to load inline banner:', err);
      }
    };
    
    const fetchSearchData = async () => {
      try {
        const trending = await searchService.getTrendingSearches(5);
        if (trending && trending.length > 0) setPopularSearches(trending);
        
        const categories = await categoryService.getAllWithCounts();
        if (categories && categories.length > 0) {
          const formattedFilters = categories
            .sort((a, b) => b.productCount - a.productCount)
            .map(cat => ({ id: cat.id, label: cat.name }));
          setQuickFilters(formattedFilters);
        }
      } catch(err) {
        console.error('Failed to load search data:', err);
      }
    }
    
    // Symmetrical Auto-Submit Product Request Draft on Login success
    const submitDraft = async () => {
      if (user && draftProductRequest) {
        try {
          const { error } = await supabase.from('product_requests').insert([{
            user_id: user.id,
            product_name: draftProductRequest.productName,
            category: draftProductRequest.category,
            manufacturer: draftProductRequest.manufacturer,
            needed_by_date: draftProductRequest.neededBy
          }]);
          if (error) throw error;
          setNotification({ show: true, message: 'Product request submitted successfully!', type: 'success' });
          setTimeout(() => setNotification({ show: false, message: '', type: '' }), 4000);
        } catch (err) {
          console.error('Failed to submit cached product request:', err);
        } finally {
          setDraftProductRequest(null);
        }
      }
    };
    submitDraft();

    fetchPublishedLayout();
    fetchBanner();
    fetchSearchData();
  }, []);

  useEffect(() => {
    if (isAuthenticated && userProfile?.raw_user_meta_data) {
        const meta = userProfile.raw_user_meta_data;
        setCategoryPreferences(meta.category_preferences || []);
        setBrandPreferences(meta.brand_preferences || []);
    }
  }, [isAuthenticated, userProfile]);

  useEffect(() => {
    if (isAuthenticated) {
      loadUserCollection();
    }
  }, [isAuthenticated]);

  const loadUserCollection = async () => {
    try {
      // Fetch ALL items regardless of source (wishlist, garage, saved_for_later)
      // This ensures the heart icon reflects "Saved" status globally
      const allItems = await userGarageService.getUserGarage(null); 
      // Extract IDs for both products and components
      const ids = allItems.map(item => item.productId || item.componentId).filter(Boolean);
      setCollectionItems(ids);
    } catch (err) {
      console.error('Failed to load user collection:', err);
    }
  };


  useEffect(() => {
    if (!initialCheckComplete) return;
    const timeout = setTimeout(() => {
      loadCatalogItems();
      if (comparison) handleUrlComparison();
    }, 50);
    return () => clearTimeout(timeout);
  }, [searchTerm, holidayParam, seasonParam, filters, sortBy, currentPage, itemsPerPage, initialCheckComplete, JSON.stringify(categoryPreferences), JSON.stringify(brandPreferences), comparison, isMobile]);


  const handleUrlComparison = async () => {
      // Expects format: slug1-vs-slug2-vs-slug3
      const slugs = comparison.split('-vs-');
      if (slugs.length > 1) {
          try {
              const modelsToCompare = await Promise.all(
                  slugs.map(slug => productService.getBySlug(slug).catch(() => null))
              );
              const validModels = modelsToCompare.filter(Boolean);
              if (validModels.length > 0) {
                  setComparisonModels(validModels);
                  setIsComparisonOpen(true);
              }
          } catch (err) {
              console.error('Failed to load comparison models:', err);
          }
      }
  };

  const loadCatalogItems = async () => {
    try {
      setIsLoading(true);
      setError('');
      const fetchFilters = {
          searchTerm,
          holiday: holidayParam,
          season: seasonParam,
          categoryIds: filters?.categories,
          manufacturerIds: filters?.manufacturers,
          priceMin: filters?.priceRange?.min,
          priceMax: filters?.priceRange?.max,
          sortBy,
          page: isMobile ? 1 : currentPage,
          itemsPerPage: isMobile ? 1000 : itemsPerPage,
          preferredCategoryNames: categoryPreferences,
          preferredBrandIds: brandPreferences
      };

      let productResult;
      let componentResult;
      let count = 0;

      // Fetch both products and components in parallel
      if (isAuthenticated) {
        [productResult, componentResult] = await Promise.all([
          productService.getAll(fetchFilters),
          componentService.getAll(fetchFilters)
        ]);
      } else {
        [productResult, componentResult] = await Promise.all([
          productService.getPublicCatalogProducts(fetchFilters),
          componentService.getAll(fetchFilters)
        ]);
      }

      // Process components
      let processedComps = (componentResult || []).map((comp) => {
        let processedComp = { ...comp, itemType: 'component' };
        if (comp.images && comp.images.length > 0) {
          const publicUrls = comp.images.map((path) => {
            if (!path) return null;
            if (path.startsWith('http')) return path;
            return imageUploadService.getPublicUrl(path);
          });
          processedComp.images = publicUrls.filter(url => url !== null);
        }
        return processedComp;
      });

      if (processedComps.length > 1000) {
         setShowResultLimitWarning(true);
         processedComps = processedComps.slice(0, 100);
      } else {
         setShowResultLimitWarning(false);
      }

      let productsArr = (productResult?.products || []).map(p => ({ ...p, itemType: 'product' }));

      // Symmetrical Promotion Filtering: Holiday
      if (holidayParam) {
        const hTerm = holidayParam.toLowerCase().trim();
        const matchesHoliday = (item) => {
          return (
            item.holiday_setting?.toLowerCase()?.includes(hTerm) ||
            item.holidaySetting?.toLowerCase()?.includes(hTerm) ||
            item.keywords?.toLowerCase()?.includes(hTerm) ||
            item.name?.toLowerCase()?.includes(hTerm) ||
            item.description?.toLowerCase()?.includes(hTerm)
          );
        };
        productsArr = productsArr.filter(matchesHoliday);
        processedComps = processedComps.filter(matchesHoliday);
      }

      // Symmetrical Promotion Filtering: Season
      if (seasonParam) {
        const sTerm = seasonParam.toLowerCase().trim();
        const matchesSeason = (item) => {
          return (
            item.seasonal_setting?.toLowerCase()?.includes(sTerm) ||
            item.seasonalSetting?.toLowerCase()?.includes(sTerm) ||
            item.keywords?.toLowerCase()?.includes(sTerm) ||
            item.name?.toLowerCase()?.includes(sTerm) ||
            item.description?.toLowerCase()?.includes(sTerm)
          );
        };
        productsArr = productsArr.filter(matchesSeason);
        processedComps = processedComps.filter(matchesSeason);
      }

      const totalProds = productResult?.totalCount !== undefined ? productResult.totalCount : productsArr.length;

      // Slice components for pagination since componentService doesn't do it on the backend
      const totalComps = processedComps.length;
      const compStart = (currentPage - 1) * itemsPerPage;
      const compEnd = compStart + itemsPerPage;
      const pageComps = processedComps.slice(compStart, compEnd);
      
      // Fetch active seller counts
      const productIds = productsArr.map(p => p.id);
      const componentIds = pageComps.map(c => c.globalComponentId || c.id);

      if (productIds.length > 0) {
        // Chunk productIds to avoid 400 Bad Request URL too long errors
        const chunkSize = 100;
        let allSps = [];
        for (let i = 0; i < productIds.length; i += chunkSize) {
           const chunk = productIds.slice(i, i + chunkSize);
           const { data: spsChunk } = await supabase
             .from('seller_products')
             .select('product_id, seller:seller_accounts!inner(id)')
             .in('product_id', chunk)
             .eq('status', 'active')
             .eq('seller.account_status', 'Active')
             .gt('price', 0)
             .or('isdeleted.is.null,isdeleted.eq.false');
           if (spsChunk) allSps = allSps.concat(spsChunk);
        }
        
        const countMap = {};
        allSps?.forEach(sp => { countMap[sp.product_id] = (countMap[sp.product_id] || 0) + 1; });
        
        // NEW: Check if product has any components with manufacturing capability
        let mfgComps = [];
        for (let i = 0; i < productIds.length; i += chunkSize) {
            const chunk = productIds.slice(i, i + chunkSize);
            const { data: chunkMfg } = await supabase
                .from('components')
                .select('product_id')
                .in('product_id', chunk)
                .or('is_3d_manufacturing.eq.true,is_cnc_capable.eq.true');
            if (chunkMfg) mfgComps = mfgComps.concat(chunkMfg);
        }
            
        const mfgMap = {};
        mfgComps?.forEach(c => { if (c.product_id) mfgMap[c.product_id] = true; });

        productsArr.forEach(p => { 
            p.activeSellerCount = countMap[p.id] || 0; 
            p.hasManufacturing = !!mfgMap[p.id];
        });
      }

      if (componentIds.length > 0) {
        const { data: scs } = await supabase
          .from('seller_components')
          .select('component_id, seller:seller_accounts!inner(id)')
          .in('component_id', componentIds)
          .eq('seller.account_status', 'Active')
          .gte('price', 0)
          .or('isdeleted.is.null,isdeleted.eq.false');
        const compCountMap = {};
        scs?.forEach(sc => { compCountMap[sc.component_id] = (compCountMap[sc.component_id] || 0) + 1; });
        pageComps.forEach(c => { c.activeSellerCount = compCountMap[c.globalComponentId || c.id] || 0; });
      }

      const isAppend = currentPage === 1 ? false : (isAppendModeRef.current || false);

      if (isAppend) {
        setProducts(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          const uniqueNew = productsArr.filter(p => !existingIds.has(p.id));
          return [...prev, ...uniqueNew];
        });
        setComponentData(prev => {
          const existingIds = new Set(prev.map(c => c.globalComponentId || c.id));
          const uniqueNew = pageComps.filter(c => !existingIds.has(c.globalComponentId || c.id));
          return [...prev, ...uniqueNew];
        });
      } else {
        setProducts(productsArr);
        setComponentData(pageComps);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

      setTotalProducts(totalProds);
      setTotalComponents(totalComps);
      setLastUpdatedTime(new Date());

      count = totalProds + totalComps;

      // Record search history if term exists and it's the first page
      if (searchTerm && searchTerm.trim() !== '' && currentPage === 1) {
        searchService.recordSearch({
          searchTerm,
          category: 'mixed_catalog',
          resultsCount: count,
          userId: user?.id
        });
      }
    } catch (err) {
      console.error('ModelCatalog: Failed to load catalog items:', err); 
      setError(err?.message || 'Failed to load catalog items. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Track Subtab Views in User Sessions
  useEffect(() => {
    if (initialCheckComplete) {
      const path = viewMode === 'component-list' ? '/model-catalog/component' : '/model-catalog/model';
      userService.recordActivity(
          user?.id || null,
          navigator.userAgent,
          'Subtab View',
          path
      ).catch(err => console.warn('Activity record failed', err));
    }
  }, [viewMode, user?.id, initialCheckComplete]);

  // Track component details view modal opens
  useEffect(() => {
    const compId = selectedDetailComponent?.globalComponentId || selectedDetailComponent?.id;
    if (compId) {
      userService.recordActivity(
        user?.id || null,
        navigator.userAgent,
        'Component View',
        `Catalog - Viewed Component: ${selectedDetailComponent.name || 'Unnamed'}`,
        null,
        compId
      ).catch(err => console.warn('Activity record failed', err));
    }
  }, [selectedDetailComponent?.globalComponentId, selectedDetailComponent?.id, user?.id]);

  const currentSearchTerm = searchTerm;
  const isSearchActive = currentSearchTerm && currentSearchTerm.trim() !== '';

  let currentDisplayedItems = isSearchActive
    ? (viewMode === 'component-list' ? [...componentData, ...products] : [...products, ...componentData])
    : (viewMode === 'component-list' ? componentData : products);

  // Symmetrical Client-Side Sorting to enforce exact order across combined arrays
  currentDisplayedItems = React.useMemo(() => {
    let items = [...currentDisplayedItems];
    if (sortBy === 'price-low') {
      items.sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0));
    } else if (sortBy === 'price-high') {
      items.sort((a, b) => (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0));
    } else if (sortBy === 'name-asc') {
      items.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else if (sortBy === 'name-desc') {
      items.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
    } else if (sortBy === 'newest') {
      items.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    } else if (sortBy === 'oldest') {
      items.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    } else if (sortBy === 'rating') {
      items.sort((a, b) => (b.average_rating || b.rating || 0) - (a.average_rating || a.rating || 0));
    } else if (sortBy === 'popularity') {
      const getScore = (item) => {
        const sales = Number(item.soldCount ?? item.sold_count ?? 0);
        const views = Number(item.combinedViews ?? item.viewsCount ?? item.views_count ?? item.views ?? 0);
        // Sales volume weighted heavily (10,000 points per sale), followed by total dynamic page views
        return (sales * 10000) + views;
      };
      items.sort((a, b) => getScore(b) - getScore(a));
    }
    return items;
  }, [currentDisplayedItems, sortBy]);
    
  // Symmetrical Mobile Category Grouping:
  // Declared symmetrically BELOW currentDisplayedItems to completely prevent any circular Temporal Dead Zone (TDZ) reference errors!
  const mobileGroupedItems = useMemo(() => {
    const grouped = {};
    const items = currentDisplayedItems || [];
    
    items.forEach(item => {
      // Resolve Category Name safely
      const categoryName = 
        item.categoryName || 
        item.category_name || 
        item.category?.name || 
        item.group || 
        item.component?.category?.name || 
        'Other Parts';

      // Symmetrically exclude 'Other Parts' from rendering as a category,
      // mapping any fallback items to the appropriate database-active category lanes
      // or filtering them cleanly to prevent clutter!
      if (categoryName === 'Other Parts' || categoryName === 'main') return;

      if (!grouped[categoryName]) {
        grouped[categoryName] = [];
      }
      grouped[categoryName].push(item);
    });
    return grouped;
  }, [currentDisplayedItems]);
    
  const hasPartialMatches = currentDisplayedItems.some(item => item.isPartialMatch);
    
  const currentTotalItems = isSearchActive 
    ? (totalProducts + totalComponents) 
    : (viewMode === 'component-list' ? totalComponents : totalProducts);
    
  const totalPages = Math.ceil(currentTotalItems / itemsPerPage);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
    
    // Update local history
    if (term && term.trim() !== '') {
      setSearchHistory((prev) => {
        const filtered = prev.filter(item => item !== term);
        const newHistory = [term, ...filtered].slice(0, 20);
        localStorage.setItem('modelCatalogSearchHistory', JSON.stringify(newHistory));
        return newHistory;
      });
    }

    const params = new URLSearchParams(searchParams);
    if (term) params?.set('search', term);
    else params?.delete('search');
    setSearchParams(params);
  };

  const handleFilterChange = (filterType, value, checked) => {
    setFilters((prev) => {
      if (filterType === 'priceRange') {
        return { ...prev, priceRange: checked ? value : null };
      } else {
        return {
          ...prev,
          [filterType]: checked ? [...prev?.[filterType], value] : prev?.[filterType]?.filter((item) => item !== value)
        };
      }
    });
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({ categories: [], manufacturers: [], priceRange: null });
    setCurrentPage(1);
  };

  const handleRemoveHistoryItem = (itemToRemove) => {
    setSearchHistory((prev) => {
      const newHistory = prev.filter((item) => item !== itemToRemove);
      localStorage.setItem('modelCatalogSearchHistory', JSON.stringify(newHistory));
      return newHistory;
    });
  };

  const handleClearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('modelCatalogSearchHistory');
  };

  const handleCategorySelect = (categoryId) => {
    setFilters(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryId) ? prev.categories : [...prev.categories, categoryId]
    }));
    setSearchTerm('');
    setCurrentPage(1);
    const params = new URLSearchParams(searchParams);
    params.delete('search');
    setSearchParams(params);
  };

  const handleSpecialFilterSelect = (type) => {
    if (type === 'new') {
      setSortBy('newest');
      setSearchTerm('');
      setCurrentPage(1);
      const params = new URLSearchParams(searchParams);
      params.delete('search');
      setSearchParams(params);
    }
  };

  const handleSelectModel = (modelId, checked) => {
    setSelectedModels((prev) => checked ? [...prev, modelId] : prev?.filter((id) => id !== modelId));
  };

  const handleSelectAll = () => {
    const allIds = products?.map((model) => model?.id);
    setSelectedModels((prev) => prev?.length === allIds?.length ? [] : allIds);
  };

  const handleBulkAction = (action, modelIds) => {
    const models = products?.filter((model) => modelIds?.includes(model?.id));
    switch (action) {
      case 'compare': 
        if (models.length > 0) {
            setComparisonModels(models); 
            setIsComparisonOpen(true); 
        }
        break;
    }
  };

  const handleAuthRequired = (message) => {
    setNotification({ show: true, message: message || "Please sign in to continue", type: 'error' });
    setAuthMode('login');
    setShowAuthModal(true);
  };

  const handleRemoveFromCollection = async (productId) => {
    if (!isAuthenticated) return false;
    try {
      setCollectionItems((prev) => prev?.filter(id => id !== productId));
      await userGarageService?.removeFromGarage(productId);
      // NOTE: userGarageService.removeFromGarage attempts both product/component if type not specified
      return true;
    } catch (err) {
      console.error("Failed to remove from collection:", err);
      // Revert optimistic update
      setCollectionItems((prev) => [...prev, productId]);
      await loadUserCollection(); // Sync with DB
      return false;
    }
  };

  const handleViewSellers = (item, isComponent) => {
    if (!item.activeSellerCount && !item.hasManufacturing && !isComponent) {
      // Navigate to 3D Viewer if no sellers
      const safeCat = (item.categoryName || item.category?.name || 'product').toLowerCase().replace(/[^a-z0-9]+/g, '-');
      let prefix = '/product';
      if (['automotive', 'motorcycles'].includes(safeCat)) prefix = '/vehicle';
      
      let processedName = item.name?.toLowerCase() || '';
      let year = '';
      if (prefix === '/vehicle') {
        const yearMatch = processedName.match(/\b(19\d{2}|20\d{2})\b/);
        if (yearMatch) {
          year = `/${yearMatch[1]}`;
          processedName = processedName.replace(yearMatch[0], '').trim();
        }
      }
      
      const pathSegments = processedName.replace(/[^a-z0-9\s.-]/g, '').trim().split(/\s+/).filter(Boolean).join('/');
      const url = `${prefix}${year}/${pathSegments}/parts-diagram`;
      window.location.href = url;
    } else {
      setSellerModalItem({ item, isComponent });
    }
  };

  const handleAddToCart = async (itemIdOrObj, isComponent = false) => {
    if (!isAuthenticated) {
      handleAuthRequired("Please sign in to add items to your cart.");
      return;
    }

    const isObj = typeof itemIdOrObj === 'object' && itemIdOrObj !== null;

    // Symmetrical offerings validation:
    // If the card is an active seller's offering (it has sellerId or its id represents the offering ID),
    // then isOffering is true, so we can skip redirecting to SellersGrid and add it to the cart directly!
    const isOffering = isObj && (
      itemIdOrObj.sellerProductId || 
      itemIdOrObj.sellerComponentId || 
      (itemIdOrObj.sellerId && itemIdOrObj.id) ||
      (isComponent && itemIdOrObj.globalComponentId && itemIdOrObj.id)
    );

    // CRITICAL: Robust check for manufacturing capability
    const is3D = isObj && (itemIdOrObj.is3dManufacturing === true || itemIdOrObj.isCncCapable === true || itemIdOrObj.preferred3dMethod || itemIdOrObj.isPainted === true || itemIdOrObj.is_painted === true);
    const hasMetadata = isObj && itemIdOrObj.metadata?.type === 'manufacturing';

    if (is3D && !hasMetadata && !isOffering) {
        // Direct both products and components to the Select Store/Offering view (SellersGrid)
        setSelectedDetailComponent(null); // Close the component detail modal!
        setPreviewModel(null);             // Close the quick preview modal!
        handleViewSellers(itemIdOrObj, isComponent);
        return;
    }

    try {
      const productId = isComponent ? null : (isObj ? (itemIdOrObj.productId || itemIdOrObj.product_id || itemIdOrObj.id) : itemIdOrObj);
      const componentId = isComponent ? (isObj ? (itemIdOrObj.globalComponentId || itemIdOrObj.componentId || itemIdOrObj.component_id || itemIdOrObj.id) : itemIdOrObj) : null;
      const sellerId = isObj ? (itemIdOrObj.sellerId || null) : null;

      const payload = {
        userId: user?.id,
        productId,
        componentId,
        sellerId,
        sellerProductId: isObj ? (itemIdOrObj.sellerProductId || (!isComponent ? itemIdOrObj.id : null)) : null,
        sellerComponentId: isObj ? (itemIdOrObj.sellerComponentId || (isComponent ? itemIdOrObj.id : null)) : null,
        quantity: 1,
        metadata: isObj ? itemIdOrObj.metadata : null
      };
      await cartService.addToCart(payload);
      
      // SUCCESS: Close modals
      setSelectedDetailComponent(null);
      setSellerModalItem(null);

      userService.recordActivity(
          user?.id || null,
          navigator.userAgent,
          'Add to Cart',
          isComponent ? `/model-catalog/component/add` : `/model-catalog/product/add`,
          productId,
          componentId
      ).catch(err => console.warn('Activity record failed', err));

      setNotification({ show: true, message: 'Item added to cart!', type: 'success' });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      setNotification({ show: true, message: error.message || 'Failed to add item to cart', type: 'error' });
    }
  };

  const handleAddToWishlist = async (modelId) => {
    if (!isAuthenticated) {
      handleAuthRequired("Please sign in to save items.");
      return;
    }
    
    // Check if already in collection
    if (collectionItems.includes(modelId)) {
        // If already there, remove it (toggle behavior)
        const success = await handleRemoveFromCollection(modelId);
        if (success) {
             setNotification({ show: true, message: 'Removed from favorites', type: 'success' });
             setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
        }
        return;
    }

    try {
      await userGarageService?.addToGarage(modelId);
      setCollectionItems((prev) => [...prev, modelId]);
      setNotification({ show: true, message: 'Added to favorites', type: 'success' });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      if (error.message && (error.message.includes('already') || error.message.includes('unique constraint'))) {
          // Graceful fallback if UI was out of sync
          setCollectionItems((prev) => {
              if (prev.includes(modelId)) return prev;
              return [...prev, modelId];
          });
          setNotification({ show: true, message: 'Already in favorites', type: 'info' });
      } else {
          setNotification({ show: true, message: 'Failed to add to favorites', type: 'error' });
      }
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    }
  };

  const handleAddToCollection = async (productId) => {
    // Legacy wrapper if needed by child components expecting this prop name
    return handleAddToWishlist(productId);
  };

  const handleQuickPreview = (model) => setPreviewModel(model);
  const handlePageChange = (page) => {
    isAppendModeRef.current = false;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLoadMore = () => {
    const currentCount = currentDisplayedItems.length;

    if (currentCount > 200 || currentPage >= totalPages) {
      // If listed items exceed 200, or we reached the end of the catalog,
      // refresh and clear the list to display the new batch of 24.
      isAppendModeRef.current = false;
      const nextPage = currentPage >= totalPages ? 1 : currentPage + 1;
      setCurrentPage(nextPage);
    } else {
      // Append the next batch of 24 items
      isAppendModeRef.current = true;
      setCurrentPage(prev => prev + 1);
    }
  };

  const canonicalUrl = comparison 
    ? `${window.location.origin}/compare/${comparison}`
    : `${window.location.origin}/model-catalog${searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : ''}`;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{comparison ? `Comparison: ${comparison.replace(/-vs-/g, ' vs ')}` : (searchTerm ? `Search results for "${searchTerm}"` : '3D Model Catalog')} | OpenGadget</title>
        <meta name="description" content={comparison ? `Compare ${comparison.replace(/-vs-/g, ' and ')} features and specifications.` : (searchTerm ? `Browse 3D models matching "${searchTerm}". Find high-quality 3D assets for your projects.` : 'Explore our extensive catalog of high-quality 3D models and interactive parts.')} />
        <link rel="canonical" href={canonicalUrl} />
        {/* Prevent indexing of highly filtered pages to avoid infinite crawl paths */}
        {(filters.categories.length > 2 || filters.manufacturers.length > 2 || currentPage > 5) && (
          <meta name="robots" content="noindex, follow" />
        )}
      </Helmet>
      <Header />
      <main className="pt-16">
        <div className="w-full px-4 py-6 md:px-6 md:py-8">
          {sellerModalItem ? (
            <div className="w-full max-w-7xl mx-auto">
              <Button 
                variant="ghost" 
                onClick={() => setSellerModalItem(null)} 
                iconName="ArrowLeft" 
                className="mb-6 -ml-2 text-muted-foreground hover:text-foreground"
              >
                Back to Catalog
              </Button>
              
              <div className="mb-8">
                <div className="flex items-center gap-4">
                  <h1 className="text-3xl font-bold text-foreground">{sellerModalItem.item.name}</h1>
                  {(sellerModalItem.item.averageRating !== undefined || sellerModalItem.item.average_rating !== undefined) && (
                    <div className="flex items-center space-x-1 bg-primary/10 px-2 py-1 rounded-md text-primary">
                      <Icon name="Star" size={20} className="text-yellow-500 fill-current" />
                      <span className="font-bold text-xl">{(sellerModalItem.item.averageRating || sellerModalItem.item.average_rating || 0)?.toFixed(1)}</span>
                      <span className="text-sm font-medium">({sellerModalItem.item.totalRatings || sellerModalItem.item.total_ratings || 0})</span>
                    </div>
                  )}
                </div>
                <p className="text-muted-foreground mt-2">Available from the following sellers:</p>
              </div>
              
              <SellersGrid 
                item={sellerModalItem.item} 
                isComponent={sellerModalItem.isComponent} 
                onAddToCart={handleAddToCart}
                isAuthenticated={isAuthenticated}
                onAuthRequired={handleAuthRequired}
                user={user}
                isAuthModalOpen={showAuthModal}
                publishedLayout={publishedLayout}
              />
            </div>
          ) : (
            <>
          <PromotedProductsBanner 
            onAddToCart={(item) => handleViewSellers(item, false)}
            onAddToWishlist={handleAddToWishlist}
            onAddToCollection={handleAddToWishlist}
            onRemoveFromCollection={handleRemoveFromCollection}
            collectionItems={collectionItems}
            isAuthenticated={isAuthenticated}
            onAuthRequired={handleAuthRequired}
            onQuickPreview={handleQuickPreview}
          />

          <div className="mb-8 w-full">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onSearch={handleSearch}
              searchHistory={searchHistory.slice(0, 10)}
              onClearHistory={handleClearHistory}
              onRemoveHistoryItem={handleRemoveHistoryItem}
              popularSearches={popularSearches}
              quickFilters={quickFilters}
              onCategorySelect={handleCategorySelect}
              onSpecialFilterSelect={handleSpecialFilterSelect}
            />
          </div>

          {isMobile && (
            <div className="mb-6 p-4 rounded-xl bg-blue-50 border border-blue-200 shadow-sm text-center">
              <h4 className="text-xs font-black uppercase text-blue-800 tracking-wider mb-2">Can't find a model?</h4>
              <Button 
                type="button" 
                variant="default" 
                size="sm" 
                className="w-full text-xs font-black uppercase tracking-widest bg-blue-600 hover:bg-blue-700 text-white shadow-md py-2.5 px-4 rounded-lg flex items-center justify-center gap-1.5 transition-all"
                onClick={() => setShowProductRequestModal(true)}
              >
                <Icon name="PlusCircle" size={14} /> Request New Product
              </Button>
            </div>
          )}

          {/* Promotional Filter Active Banner */}
          {(holidayParam || seasonParam) && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-3.5 mb-6 flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-2.5 flex-wrap">
                <span className="text-xs font-bold uppercase tracking-wider text-amber-900 flex items-center gap-1.5">
                  <Icon name="Tag" size={14} className="text-amber-600" />
                  Promotion Filter:
                </span>
                {holidayParam && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-orange-500 text-white shadow-sm">
                    <span>Holiday: <strong className="capitalize">{holidayParam}</strong></span>
                    <button
                      type="button"
                      onClick={() => {
                        const newParams = new URLSearchParams(searchParams);
                        newParams.delete('holiday');
                        newParams.delete('holidy');
                        setSearchParams(newParams);
                      }}
                      className="hover:bg-orange-600 p-0.5 rounded-full transition-colors cursor-pointer"
                      title="Clear holiday filter"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </span>
                )}
                {seasonParam && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-600 text-white shadow-sm">
                    <span>Season: <strong className="capitalize">{seasonParam}</strong></span>
                    <button
                      type="button"
                      onClick={() => {
                        const newParams = new URLSearchParams(searchParams);
                        newParams.delete('season');
                        setSearchParams(newParams);
                      }}
                      className="hover:bg-amber-700 p-0.5 rounded-full transition-colors cursor-pointer"
                      title="Clear season filter"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('holiday');
                  newParams.delete('holidy');
                  newParams.delete('season');
                  setSearchParams(newParams);
                }}
                className="text-xs font-bold text-amber-900 hover:text-amber-950 underline cursor-pointer ml-3"
              >
                Clear All
              </button>
            </div>
          )}

          <div className="flex gap-6">
            {isMobile ? (
              activeMobileCategory ? (() => {
                const categoryItems = mobileGroupedItems[activeMobileCategory] || [];
                const slicedCategoryItems = categoryItems.slice(0, mobileCategoryLimit);
                const hasMoreCategoryItems = categoryItems.length > mobileCategoryLimit;

                return (
                  /* Symmetrical Category-Only Standard Grid View */
                  <div className="flex-1 min-w-0 space-y-6 mt-2 max-w-full">
                    <div className="flex items-center gap-3 border-b border-border/40 pb-4">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => {
                          setActiveMobileCategory(null);
                          setMobileCategoryLimit(30); // Reset page limit on back!
                        }} 
                        className="shrink-0 p-1 px-2.5 h-8 flex items-center justify-center text-xs text-muted-foreground hover:text-foreground"
                      >
                        <Icon name="ArrowLeft" size={16} className="mr-1" />
                        Back
                      </Button>
                      <h3 className="text-sm font-black text-foreground uppercase tracking-widest leading-none">
                        {activeMobileCategory}
                      </h3>
                    </div>

                    <div className="grid grid-cols-2 gap-x-4 gap-y-6">
                      {slicedCategoryItems.map((item) => {
                        const isComponent = item.itemType === 'component';
                        const linkUrl = isComponent 
                          ? `/3d-model-viewer?componentId=${item.id}&componentName=${encodeURIComponent(item.name)}`
                          : `/3d-model-viewer?id=${item.id}`;
                        const thumbnail = item.thumbnailUrl || item.thumbnailPath || "/assets/images/model-fallback.jpg";

                        return (
                          <Link 
                            key={item.id}
                            to={linkUrl}
                            className="flex flex-col hover:opacity-85 active:scale-95 transition-all duration-200"
                          >
                            {/* 1:1 Aspect Ratio Image Anchor Frame (Fully borderless, backgroundless!) */}
                            <div className="w-full aspect-square relative flex items-center justify-center overflow-hidden">
                              <img 
                                src={thumbnail} 
                                alt={item.name}
                                className="w-full h-full object-contain"
                                loading="lazy"
                              />
                            </div>
                            {/* Flat Minimalist Title beneath Image */}
                            <div className="p-1 pt-2 flex-1 flex flex-col justify-start">
                              <p className="text-[10px] font-bold text-foreground line-clamp-2 leading-tight uppercase tracking-wider text-center" title={item.name}>
                                {item.name}
                              </p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>

                    {/* View More button at the bottom of the grid to load the next 30 items! */}
                    {hasMoreCategoryItems && (
                      <div className="flex justify-center mt-6">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setMobileCategoryLimit(prev => prev + 30)}
                          className="text-xs font-black uppercase tracking-widest px-6"
                        >
                          View More...
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })() : (
                /* Symmetrical Minimalist Mobile Category Lanes Dashboard View */
                <div className="flex-1 min-w-0 space-y-8 mt-2 max-w-full overflow-hidden">
                  {Object.entries(mobileGroupedItems).map(([categoryName, items]) => {
                    if (items.length === 0) return null;
                    
                    // Limit each swipe row to 20 models as requested!
                    const slicedItems = items.slice(0, 20);
                    const hasMore = items.length > 20;

                    return (
                      <div key={categoryName} className="space-y-3 max-w-full overflow-hidden">
                        <h4 className="text-xs font-black text-muted-foreground uppercase tracking-widest px-1">
                          {categoryName}
                        </h4>
                        
                        {/* Horizontal Swipe Lane Container */}
                        <div className="flex overflow-x-auto gap-2.5 pb-4 snap-x snap-mandatory scrollbar-none max-w-full px-1">
                          {slicedItems.map((item) => {
                            const isComponent = item.itemType === 'component';
                            const linkUrl = isComponent 
                              ? `/3d-model-viewer?componentId=${item.id}&componentName=${encodeURIComponent(item.name)}`
                              : `/3d-model-viewer?id=${item.id}`;
                            const thumbnail = item.thumbnailUrl || item.thumbnailPath || "/assets/images/model-fallback.jpg";

                            return (
                              <Link 
                                key={item.id}
                                to={linkUrl}
                                className="flex-shrink-0 w-40 snap-start flex flex-col hover:opacity-85 active:scale-95 transition-all duration-200"
                              >
                                {/* 1:1 Aspect Ratio Image Anchor Frame (Fully borderless, backgroundless!) */}
                                <div className="w-full aspect-square relative flex items-center justify-center overflow-hidden">
                                  <img 
                                    src={thumbnail} 
                                    alt={item.name}
                                    className="w-full h-full object-contain"
                                    loading="lazy"
                                  />
                                </div>
                                {/* Flat Minimalist Title beneath Image */}
                                <div className="p-1 pt-2.5 flex-1 flex flex-col justify-start">
                                  <p className="text-[10px] font-bold text-foreground line-clamp-2 leading-tight uppercase tracking-wider text-center" title={item.name}>
                                    {item.name}
                                  </p>
                                </div>
                              </Link>
                            );
                          })}

                          {/* Present a gorgeous "View All" blank category card for every active category lane */}
                          {items.length > 0 && (
                            <button 
                              onClick={() => setActiveMobileCategory(categoryName)}
                              className="flex-shrink-0 w-40 aspect-square snap-start border-2 border-dashed border-border/80 rounded-2xl flex flex-col items-center justify-center hover:bg-muted/10 transition-all active:scale-95 duration-200 gap-1.5"
                            >
                              <div className="bg-muted/20 p-2 rounded-full border border-border/40">
                                <Icon name="ArrowRight" size={18} className="text-muted-foreground" />
                              </div>
                              <span className="text-[9px] font-black text-muted-foreground uppercase tracking-widest text-center px-1 break-words max-w-full">
                                View More {categoryName}'s
                              </span>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {Object.keys(mobileGroupedItems).length === 0 && (
                    <div className="text-center py-16 bg-muted/10 border border-border border-dashed rounded-2xl p-6">
                      <Icon name="PackageX" size={48} className="mx-auto text-muted-foreground mb-4 opacity-50" />
                      <h3 className="text-base font-semibold text-foreground mb-1">No matches found</h3>
                      <p className="text-xs text-muted-foreground max-w-xs mx-auto leading-relaxed">
                        We couldn't find any products or components matching "{searchTerm}". Try refining your keywords.
                      </p>
                    </div>
                  )}
                </div>
              )
            ) : (
              /* Standard Desktop FilterSidebar and Results Grid */
              <>
                <FilterSidebar
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={handleClearFilters}
                  isOpen={isFilterSidebarOpen}
                  onToggle={() => setIsFilterSidebarOpen(!isFilterSidebarOpen)}
                  viewMode={viewMode}
                  onRequestProductClick={() => {
                    console.log('🎯 [ModelCatalog Index] Setting showProductRequestModal to true!');
                    setShowProductRequestModal(true);
                  }}
                />

                <div className="flex-1 min-w-0">
                  {!isAuthenticated && initialCheckComplete && (
                    <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6 flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-primary mb-1">Create an account to unlock more features</h3>
                        <p className="text-sm text-muted-foreground">Save your favorite models, create your own garage, and more.</p>
                      </div>
                      <Button variant="default" size="sm" onClick={() => { setAuthMode('signup'); setShowAuthModal(true); }}>Sign Up</Button>
                    </div>
                  )}
                  <ViewControls
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    sortBy={sortBy}
                    onSearchChange={setSearchTerm}
                    onSortChange={(val) => {
                      isAppendModeRef.current = false;
                      setCurrentPage(1);
                      setSortBy(val);
                    }}
                    itemsPerPage={itemsPerPage}
                    onItemsPerPageChange={(val) => {
                      isAppendModeRef.current = false;
                      setCurrentPage(1);
                      setItemsPerPage(val);
                    }}
                    totalItems={currentTotalItems}
                    itemType={viewMode === 'component-list' ? 'components' : 'models'}
                    selectedItems={selectedModels}
                    onOpenComparison={() => setIsComparisonOpen(true)}
                    onClearSelection={() => setSelectedModels([])}
                    onBulkAction={handleBulkAction}
                    lastUpdatedTime={lastUpdatedTime} />

                  {hasPartialMatches && (
                    <div className="bg-blue-500/10 border border-blue-500/30 text-blue-600 dark:text-blue-400 p-3 rounded-lg mb-6 flex items-start gap-3">
                      <Icon name="Info" size={20} className="shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-sm">No Exact Matches</h4>
                        <p className="text-sm">We couldn't find an exact match for all your terms, but here are some related items that match one or more of your keywords.</p>
                      </div>
                    </div>
                  )}

                  {showResultLimitWarning && viewMode === 'component-list' && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-600 dark:text-yellow-400 p-3 rounded-lg mb-6 flex items-start gap-3">
                      <Icon name="AlertTriangle" size={20} className="shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-sm">Too many results</h4>
                        <p className="text-sm">There are more than 1000 items matching your search. Please refine your search terms or filters to narrow down the results. Showing the first 100 items.</p>
                      </div>
                    </div>
                  )}

                  {error && (
                    <div className="bg-destructive/10 border border-destructive rounded-lg p-4 mb-6">
                      <div className="flex items-start gap-3">
                        <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-destructive mb-1">Error Loading Products</h3>
                          <p className="text-sm text-muted-foreground">{error}</p>
                          <Button variant="outline" size="sm" onClick={loadCatalogItems} iconName="RefreshCw" iconPosition="left">Retry</Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {isLoading && currentDisplayedItems.length === 0 && (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-center">
                        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-muted-foreground">Loading...</p>
                      </div>
                    </div>
                  )}

                  {!error && (currentDisplayedItems.length > 0 || !isLoading) && (
                    <>
                      {currentDisplayedItems.length > 0 ? (
                        <div 
                          ref={gridRef}
                          className={gridStyle ? "grid" : (viewMode === 'grid' || viewMode === 'component-list' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 min-[1920px]:grid-cols-6 gap-6' : 'space-y-4')}
                          style={gridStyle}
                        >
                          {(() => {
                            const itemsToRender = [...currentDisplayedItems];
                            if (inlineBanner && itemsToRender.length > 0) {
                               const colCount = Math.max(1, columns || 1);
                               const availableCompleteRows = Math.floor(itemsToRender.length / colCount);
                               const rowsBeforeBanner = Math.min(3, Math.max(1, availableCompleteRows));
                               const insertIdx = rowsBeforeBanner * colCount;
                               itemsToRender.splice(insertIdx, 0, { isBanner: true, ...inlineBanner });
                            }
                            return itemsToRender.map((item, index) => {
                              if (item.isBanner) {
                                 return <div key={`banner-${index}`} className="col-span-full w-full"><InlineBanner banner={item} isAuthenticated={isAuthenticated} onAuthRequired={handleAuthRequired} /></div>;
                              }
                              
                              // Component check
                              if (item.itemType === 'component') {
                                return (
                                  <ComponentCard
                                    key={`comp-${item.id}`}
                                    component={item}
                                    cardLayout={publishedLayout}
                                    onAddToCart={(comp) => handleViewSellers(comp, true)}
                                    onDirectAddToCart={(comp) => handleAddToCart(comp, true)}
                                    isAuthenticated={isAuthenticated}
                                    onAuthRequired={handleAuthRequired}
                                    onViewDetails={(comp) => setSelectedDetailComponent(comp)}
                                    onAddToWishlist={(id) => handleAddToWishlist(id, true)}
                                    onRemoveFromCollection={handleRemoveFromCollection}
                                    collectionItems={collectionItems}
                                  />
                                );
                              }

                              // Product check
                              return (
                                <ModelCard
                                  key={`prod-${item.id}`}
                                  model={item}
                                  viewMode={viewMode === 'component-list' ? 'grid' : viewMode}
                                  cardLayout={publishedLayout}
                                  onAddToWishlist={handleAddToWishlist}
                                  onAddToCollection={handleAddToWishlist}
                                  onRemoveFromCollection={handleRemoveFromCollection}
                                  isInCollection={collectionItems.includes(item.id)}
                                  onQuickPreview={handleQuickPreview}
                                  isSelected={selectedModels.includes(item.id)}
                                  onSelect={handleSelectModel}
                                  onAddToCart={(item) => handleViewSellers(item, false)}
                                  isAuthenticated={isAuthenticated}
                                  onAuthRequired={handleAuthRequired}
                                  isGlobalCatalog={true}
                                />
                              );
                            });
                          })()}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <Icon name="Search" size={48} className="mx-auto text-muted-foreground mb-4" />
                          <h3 className="text-lg font-medium text-foreground mb-2">No results found</h3>
                          <p className="text-muted-foreground mb-4">Try adjusting your search terms or filters.</p>
                          <Button variant="outline" onClick={handleClearFilters} iconName="RotateCcw" iconPosition="left">Clear Filters</Button>
                        </div>
                      )}

                      {/* Keep the pagination navigation code commented out to use at a later date
                      {totalPages > 1 && (
                        <div className="flex items-center justify-center space-x-2 mt-8">
                          <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} iconName="ChevronLeft" />
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            const page = i + 1;
                            return <Button key={page} variant={currentPage === page ? 'default' : 'outline'} size="sm" onClick={() => handlePageChange(page)}>{page}</Button>;
                          })}
                          {totalPages > 5 && currentPage < totalPages - 2 && <><span className="text-muted-foreground">...</span><Button variant="outline" size="sm" onClick={() => handlePageChange(totalPages)}>{totalPages}</Button></>}
                          <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} iconName="ChevronRight" />
                        </div>
                      )}
                      */}

                      {totalPages > 1 && (
                        <div className="flex items-center justify-center mt-8">
                          <Button
                            variant="default"
                            onClick={handleLoadMore}
                            disabled={isLoading}
                            iconName={isLoading ? "RefreshCw" : "ChevronDown"}
                            iconPosition="left"
                            className={isLoading ? "animate-spin" : ""}
                          >
                            {isLoading ? "Loading..." : "Load More..."}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
            </>
          )}
        </div>
      </main>
      <QuickPreviewModal model={previewModel} isOpen={!!previewModel} onClose={() => setPreviewModel(null)} onAddToCart={(item) => handleViewSellers(item, false)} onAddToWishlist={handleAddToWishlist} onAddToCollection={handleAddToWishlist} onRemoveFromCollection={handleRemoveFromCollection} isInCollection={previewModel ? collectionItems.includes(previewModel.id) : false} isAuthenticated={isAuthenticated} onAuthRequired={handleAuthRequired} isGlobalCatalog={true} /> 
      <ComponentDetailModal 
        component={selectedDetailComponent} 
        isOpen={!!selectedDetailComponent} 
        onClose={() => setSelectedDetailComponent(null)} 
        onAddToCart={(item) => handleAddToCart(item, true)}
        onDirectAddToCart={(item) => handleAddToCart(item, true)}
        isAuthenticated={isAuthenticated}
        onAuthRequired={handleAuthRequired}
        isGlobalCatalog={true}
      />
      <ComparisonTool 
        models={comparisonModels} 
        isOpen={isComparisonOpen} 
        onClose={() => {
            setIsComparisonOpen(false);
            setComparisonModels([]);
            if (comparison) {
               navigate('/model-catalog', { replace: true });
            }
        }} 
        onRemoveModel={(modelId) => setComparisonModels((prev) => prev.filter((m) => m.id !== modelId))} 
        onAddToCart={(item) => handleViewSellers(item, false)} 
      />
      <LoginSignupModal isOpen={showAuthModal} initialMode={authMode} onClose={() => setShowAuthModal(false)} onSuccess={() => setShowAuthModal(false)} />
      
      {/* Notification Toast */}
      {notification.show && (
        <div className={`fixed bottom-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg text-white transform transition-all duration-300 ${
          notification.type === 'error' ? 'bg-red-600' : 'bg-green-600'
        }`}>
          <div className="flex items-center gap-2">
            <Icon name={notification.type === 'error' ? 'AlertCircle' : 'Check'} size={20} />
            <span>{notification.message}</span>
            <button onClick={() => setNotification({ ...notification, show: false })} className="ml-4 hover:opacity-80">
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>
      )}

      <ProductRequestModal
        isOpen={showProductRequestModal}
        onClose={() => setShowProductRequestModal(false)}
        user={user}
        onSubmitDraft={(draft) => {
          setDraftProductRequest(draft);
          setAuthMode('signin');
          setShowAuthModal(true);
        }}
      />
    </div>
  );
};


const SellersGrid = ({ item, isComponent, onAddToCart, isAuthenticated, onAuthRequired, user, isAuthModalOpen, publishedLayout = null }) => {
  const [selectedOffering, setSelectedOffering] = React.useState(null);
  const [sellers, setSellers] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [conditionFilter, setConditionFilter] = React.useState('All');
  const [suggestions, setSuggestions] = React.useState([]);

  React.useEffect(() => {
    if (item) {
      // Record activity for viewing sellers
      userService.recordActivity(
          user?.id || null,
          navigator.userAgent,
          'Sellers View',
          isComponent ? `/model-catalog/component/sellers` : `/model-catalog/product/sellers`,
          isComponent ? null : item.id,
          isComponent ? (item.globalComponentId || item.id) : null
      ).catch(err => console.warn('Activity record failed', err));

      const fetchSellers = async () => {
        setIsLoading(true);
        try {
          
          let data = [];
          if (isComponent) {
            const { data: sc } = await supabase
              .from('seller_components')
              .select('id, price, stock_quantity, purchase_limit, custom_name, condition, average_rating, total_ratings, is_self_manufacturing, manufacturer_id, seller:seller_accounts!inner(id, business_name, business_slug, average_rating, total_ratings, account_status), images_url, component:components(manufacturer:manufacturers(id, name, slug), is_3d_manufacturing, is_cnc_capable, mfg_3d_materials, mfg_3d_colors, mfg_cnc_materials, mfg_cnc_colors, stl_file_path), manufacturing_options(offered_methods, mfg_3d_types, mfg_3d_materials, mfg_3d_colors, mfg_cnc_types, mfg_cnc_materials, mfg_cnc_colors, is_painted, painted_colors, stl_file_path)')
              .eq('component_id', item.globalComponentId || item.id)
              .eq('status', 'active')
              .eq('seller.account_status', 'Active')
              .gte('price', 0)
              .or('isdeleted.is.null,isdeleted.eq.false');
            data = sc || [];
          } else {
            const { data: sp } = await supabase
              .from('seller_products')
              .select('id, price, stock_amount, purchase_limit, vin_sn, seller_image_url, condition, is_self_manufacturing, manufacturer_id, seller:seller_accounts!inner(id, business_name, business_slug, average_rating, total_ratings, account_status), manufacturing_options(offered_methods, mfg_3d_types, mfg_3d_materials, mfg_3d_colors, mfg_cnc_types, mfg_cnc_materials, mfg_cnc_colors, is_painted, painted_colors, stl_file_path)')
              .eq('product_id', item.id)
              .eq('status', 'active')
              .eq('seller.account_status', 'Active')
              .gt('price', 0)
              .or('isdeleted.is.null,isdeleted.eq.false');
            data = sp || [];
          }

          const formatted = data.map(offering => {
            const rawImage = isComponent ? offering.images_url?.[0] : offering.seller_image_url?.[0];
            let resolvedImage = null;
            if (rawImage) {
               resolvedImage = (rawImage.startsWith('http') || rawImage.startsWith('/assets') || rawImage.startsWith('/api')) 
                 ? rawImage 
                 : imageUploadService.getPublicUrl(rawImage);
            }
            if (!resolvedImage) {
               if (isComponent) {
                 resolvedImage = item.images?.[0] || item.product?.thumbnailUrl || "/assets/images/no_image.png";
               } else {
                 resolvedImage = item.thumbnailUrl || item.imageUrls?.[0] || "/assets/images/no_image.png";
               }
            }

            const mfgOpt = Array.isArray(offering.manufacturing_options) ? offering.manufacturing_options[0] : offering.manufacturing_options;
            const is3d = mfgOpt ? (mfgOpt.offered_methods === '3d_only' || mfgOpt.offered_methods === 'both') : false;
            const isCnc = mfgOpt ? (mfgOpt.offered_methods === 'cnc_only' || mfgOpt.offered_methods === 'both') : false;

            const isPartnerDelegated = offering.is_self_manufacturing === false && !!offering.manufacturer_id;

            return {
              ...offering,
              vinSn: offering.vin_sn,
              is3dManufacturing: isPartnerDelegated ? true : (is3d || offering.component?.is_3d_manufacturing || item?.is3dManufacturing || item?.is_3d_manufacturing || false),
              isCncCapable: isPartnerDelegated ? true : (isCnc || offering.component?.is_cnc_capable || item?.isCncCapable || item?.is_cnc_capable || false),
              mfg3dTypes: mfgOpt?.mfg_3d_types || offering.component?.mfg_3d_types || [],
              mfg3dMaterials: mfgOpt?.mfg_3d_materials || offering.component?.mfg_3d_materials,
              mfg3dColors: mfgOpt?.mfg_3d_colors || offering.component?.mfg_3d_colors,
              mfgCncTypes: mfgOpt?.mfg_cnc_types || offering.component?.mfg_cnc_types || [],
              mfgCncMaterials: mfgOpt?.mfg_cnc_materials || offering.component?.mfg_cnc_materials,
              mfgCncColors: mfgOpt?.mfg_cnc_colors || offering.component?.mfg_cnc_colors,
              isPainted: mfgOpt?.is_painted || false,
              paintedColors: mfgOpt?.painted_colors || [],
              stlFilePath: mfgOpt?.stl_file_path || '',
              stl_file_path: mfgOpt?.stl_file_path || '',
              globalItem: {
                 ...item,
                 // Overwrite globalItem manufacturer with explicit join if it was omitted
                 manufacturer: offering?.manufacturer || offering?.component?.manufacturer || item?.manufacturer
              },
              imageUrl: resolvedImage
            };
          });

          let recommended = [];
          const activeSellerId = formatted[0]?.seller?.id || null;
          if (isComponent && activeSellerId) {
             const res = await componentService.getAll({ limit: 6, sellerId: activeSellerId });
             recommended = res || [];
          } else if (!isComponent && activeSellerId) {
             const res = await sellerProductService.getPublicSellerProductsBySellerId(activeSellerId);
             recommended = (res || []).map(p => ({
                 id: p.productId,
                 sellerProductId: p.spId,
                 name: p.pName,
                 description: p.description || p.pDescription,
                 price: p.spPrice,
                 thumbnailPath: p.pThumbnailPath,
                 viewsCount: p.viewsCount || 0,
                 averageRating: p.pAverageRating,
                 totalRatings: p.pTotalRatings,
                 componentCount: p.pComponentCount,
                 isCertified: p.pIsCertified,
                 isActive: p.status === 'active',
                 status: p.status
             }));
          } else {
             const res = await productService.getTrending(4);
             recommended = res || [];
          }
          setSuggestions(recommended.filter(r => r.id !== (item.globalComponentId || item.id) && r.isActive !== false && r.status !== 'inactive').slice(0, 3));

          if (formatted.length === 0) {
            setSellers([]);
          } else {
            setSellers(formatted.sort((a, b) => (a.price || 0) - (b.price || 0)));
          }
        } catch (err) {
          console.error('Error fetching sellers:', err);
        } finally {
          setIsLoading(false);
        }
      };
      fetchSellers();
    }
  }, [item, isComponent]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4" />
        <p className="text-muted-foreground">Loading active sellers...</p>
      </div>
    );
  }

  if (sellers.length === 0) {
    return (
      <div className="flex flex-col items-center">
        <div className="text-center py-16 w-full bg-muted/50 rounded-xl border border-border mb-8">
          <h3 className="text-xl font-medium text-foreground mb-2">No Active Sellers</h3>
          <p className="text-muted-foreground">This item is currently not offered directly by any seller.</p>
        </div>
        
        {suggestions.length > 0 && (
          <div className="w-full text-left mt-4 border-t border-border pt-8">
             <h4 className="text-lg font-semibold text-foreground mb-4">You might also be interested in:</h4>
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {suggestions.map(s => isComponent ? (
                   <ComponentCard 
                     key={s.id} 
                     component={s} 
                     isGlobalCatalog={true}
                     cardLayout={publishedLayout}
                     onAddToCart={(comp) => {
                        if (comp.is3dManufacturing || comp.isCncCapable || comp.isPainted) {
                           setSelectedOffering(comp);
                        } else {
                           onAddToCart(comp, true);
                        }
                     }}
                     isAuthenticated={isAuthenticated}
                     onAuthRequired={onAuthRequired}
                     onViewDetails={() => {
                        window.location.href = `/product/${s.id}/parts-diagram`;
                     }} 
                   />
                ) : (
                   <ModelCard 
                     key={s.id} 
                     model={s} 
                     isGlobalCatalog={true} 
                     cardLayout={publishedLayout}
                     onAddToCart={onAddToCart}
                     isAuthenticated={isAuthenticated}
                     onAuthRequired={onAuthRequired}
                   />
                ))}
             </div>
          </div>
        )}
      </div>
    );
  }

  const filteredSellers = sellers.filter(s => conditionFilter === 'All' || (s.condition || 'New').toLowerCase() === conditionFilter.toLowerCase());

  return (
    <div>
      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
        <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">Condition:</span>
        {['All', 'New', 'Used', 'Refurbished'].map(cond => (
          <Button
            key={cond}
            variant={conditionFilter === cond ? 'default' : 'outline'}
            size="sm"
            onClick={() => setConditionFilter(cond)}
            className="rounded-full whitespace-nowrap"
          >
            {cond}
          </Button>
        ))}
      </div>

      {filteredSellers.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-xl border border-border/50">
          <p className="text-muted-foreground">No sellers found offering this item in "{conditionFilter}" condition.</p>
          <Button variant="link" onClick={() => setConditionFilter('All')} className="mt-2">Clear Filter</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
          {filteredSellers.map((offering) => (
            <SellerOfferingCard
              key={offering.id}
              offering={offering}
              onAddToCart={onAddToCart}
              isAuthenticated={isAuthenticated}
              onAuthRequired={onAuthRequired}
              isComponent={isComponent}
              onViewDetails={() => setSelectedOffering(offering)}
            />
          ))}
        </div>
      )}
      
      {suggestions.length > 0 && (
          <div className="w-full text-left mt-12 pt-8 border-t border-border">
             <h4 className="text-lg font-semibold text-foreground mb-4">You might also be interested in:</h4>
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {suggestions.map(s => isComponent ? (
                   <ComponentCard 
                     key={s.id} 
                     component={s} 
                     isGlobalCatalog={true}
                     cardLayout={publishedLayout}
                     onAddToCart={(comp) => {
                        if (comp.is3dManufacturing || comp.isCncCapable || comp.isPainted) {
                           setSelectedOffering(comp);
                        } else {
                           onAddToCart(comp, true);
                        }
                     }}
                     isAuthenticated={isAuthenticated}
                     onAuthRequired={onAuthRequired}
                     onViewDetails={() => {
                        window.location.href = `/product/${s.id}/parts-diagram`;
                     }} 
                   />
                ) : (
                   <ModelCard 
                     key={s.id} 
                     model={s} 
                     isGlobalCatalog={true} 
                     cardLayout={publishedLayout}
                     onAddToCart={onAddToCart}
                     isAuthenticated={isAuthenticated}
                     onAuthRequired={onAuthRequired}
                   />
                ))}
             </div>
          </div>
        )}

      {selectedOffering && (
        <SellerOfferingDetailModal
          isOpen={!!selectedOffering}
          offering={selectedOffering}
          isComponent={isComponent}
          parentModelUrl={isComponent ? (item?.product?.modelUrl || item?.product?.gltfFilePath || null) : (item?.modelUrl || item?.gltfFilePath || null)}
          onClose={() => setSelectedOffering(null)}
          onAddToCart={onAddToCart}
          isAuthenticated={isAuthenticated}
          onAuthRequired={onAuthRequired}
          isAuthModalOpen={isAuthModalOpen}
        />
      )}
    </div>
  );
};

export default ModelCatalog;